import React, { useEffect, useMemo, useState } from "react";
import LeftMenu from "../../components/left-menu/index.js";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import "./style.css";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";

import {
  Select,
  MenuItem,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Button,
  Typography,
  Card,
  TextField,
  Box,
  LinearProgress,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import * as XLSX from "xlsx";

import InquiryPops from "../../components/InquiryPops/index";

const EMPTY_MONTHLY_FUNNEL = {
  totalCreated: 0,
  stages: {
    newInquiryDeals: { label: "New Inquiry Deals", count: 0, percent: 0 },
    contacted: { label: "Contacted", count: 0, percent: 0 },
    offered: { label: "Offered", count: 0, percent: 0 },
    invoiced: { label: "Invoiced", count: 0, percent: 0 },
    paid: { label: "Paid", count: 0, percent: 0 },
  },
  outcomes: {
    spam: {
      label: "Spam",
      count: 0,
      percent: 0,
      from: {
        newInquiryDeals: { count: 0, percent: 0 },
        contacted: { count: 0, percent: 0 },
        offered: { count: 0, percent: 0 },
        invoiced: { count: 0, percent: 0 },
        paid: { count: 0, percent: 0 },
      },
    },
    cold: {
      label: "Cold",
      count: 0,
      percent: 0,
      from: {
        newInquiryDeals: { count: 0, percent: 0 },
        contacted: { count: 0, percent: 0 },
        offered: { count: 0, percent: 0 },
        invoiced: { count: 0, percent: 0 },
        paid: { count: 0, percent: 0 },
      },
    },
    partner: {
      label: "Kerja Sama",
      count: 0,
      percent: 0,
      from: {
        newInquiryDeals: { count: 0, percent: 0 },
        contacted: { count: 0, percent: 0 },
        offered: { count: 0, percent: 0 },
        invoiced: { count: 0, percent: 0 },
        paid: { count: 0, percent: 0 },
      },
    },
  },
};

function buildCurvePath(start, end, side) {
  const dx = Math.abs(end.x - start.x);
  const cp1x = side === "left" ? start.x - dx * 0.35 : start.x + dx * 0.35;
  const cp2x = side === "left" ? end.x + dx * 0.35 : end.x - dx * 0.35;

  return `M ${start.x} ${start.y} C ${cp1x} ${start.y}, ${cp2x} ${end.y}, ${end.x} ${end.y}`;
}

function FunnelShape({
  layout,
  color,
  label,
  count,
  percent,
  countFontSize,
  labelFontSize,
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: layout.x,
        top: layout.y,
        width: layout.width,
        height: layout.height,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "#2c2c2c",
          clipPath: layout.clipPath,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 3,
          backgroundColor: color,
          clipPath: layout.clipPath,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: countFontSize,
            fontWeight: 800,
            color: "#222",
            lineHeight: 1,
          }}
        >
          {count} ({percent}%)
        </Typography>

        <Typography
          sx={{
            mt: 1.5,
            fontSize: labelFontSize,
            fontWeight: 800,
            color: "#222",
            lineHeight: 1.05,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function SideOutcomeBox({ layout, color, label, count, percent }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: layout.x,
        top: layout.y,
        width: layout.width,
        height: layout.height,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          border: "3px solid #2c2c2c",
          backgroundColor: color,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          borderRadius: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1,
          }}
        >
          {count} ({percent}%)
        </Typography>

        <Typography
          sx={{
            mt: 1,
            fontSize: 24,
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1.05,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function MonthlyFunnelDiagram({ funnel }) {
  const stages = funnel?.stages || EMPTY_MONTHLY_FUNNEL.stages;
  const outcomes = funnel?.outcomes || EMPTY_MONTHLY_FUNNEL.outcomes;

  const canvasWidth = 1320;
  const canvasHeight = 900;

  const stageLayouts = {
    newInquiryDeals: {
      x: 425,
      y: 18,
      width: 470,
      height: 185,
      clipPath: "polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)",
      color: "#6b96e5",
      countFontSize: 54,
      labelFontSize: 40,
    },
    contacted: {
      x: 485,
      y: 220,
      width: 350,
      height: 165,
      clipPath: "polygon(0% 0%, 100% 0%, 92% 100%, 8% 100%)",
      color: "#35c759",
      countFontSize: 42,
      labelFontSize: 34,
    },
    offered: {
      x: 535,
      y: 400,
      width: 250,
      height: 135,
      clipPath: "polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)",
      color: "#f2d64b",
      countFontSize: 34,
      labelFontSize: 28,
    },
    invoiced: {
      x: 575,
      y: 550,
      width: 170,
      height: 110,
      clipPath: "polygon(0% 0%, 100% 0%, 88% 100%, 12% 100%)",
      color: "#f5a04e",
      countFontSize: 28,
      labelFontSize: 22,
    },
    paid: {
      x: 588,
      y: 675,
      width: 144,
      height: 180,
      clipPath: "polygon(12% 0%, 88% 0%, 58% 100%, 42% 100%)",
      color: "#ff666c",
      countFontSize: 22,
      labelFontSize: 20,
    },
  };

  const outcomeLayouts = {
    spam: {
      x: 55,
      y: 265,
      width: 210,
      height: 170,
      color: "#5f5f5f",
    },
    cold: {
      x: 1055,
      y: 265,
      width: 210,
      height: 170,
      color: "#355eaf",
    },
    partner: {
      x: 1055,
      y: 520,
      width: 210,
      height: 170,
      color: "#6a4fb3",
    },
  };

  const stageAnchors = {
    newInquiryDeals: {
      left: { x: 452, y: 120 },
      right: { x: 868, y: 120 },
    },
    contacted: {
      left: { x: 499, y: 305 },
      right: { x: 821, y: 305 },
    },
    offered: {
      left: { x: 548, y: 470 },
      right: { x: 772, y: 470 },
    },
    invoiced: {
      left: { x: 589, y: 605 },
      right: { x: 731, y: 605 },
    },
    paid: {
      left: { x: 610, y: 765 },
      right: { x: 710, y: 765 },
    },
  };

  const destinationSlots = {
    spam: {
      side: "left",
      x: outcomeLayouts.spam.x + outcomeLayouts.spam.width,
      yByOrigin: {
        newInquiryDeals: 300,
        contacted: 350,
        offered: 390,
        invoiced: 420,
        paid: 440,
      },
    },
    cold: {
      side: "right",
      x: outcomeLayouts.cold.x,
      yByOrigin: {
        newInquiryDeals: 300,
        contacted: 350,
        offered: 390,
        invoiced: 420,
        paid: 440,
      },
    },
    partner: {
      side: "right",
      x: outcomeLayouts.partner.x,
      yByOrigin: {
        newInquiryDeals: 550,
        contacted: 585,
        offered: 620,
        invoiced: 655,
        paid: 685,
      },
    },
  };

  const connections = [
    ...["newInquiryDeals", "contacted", "offered", "invoiced"].flatMap(
      (originKey) => {
        const outcome = outcomes.spam || EMPTY_MONTHLY_FUNNEL.outcomes.spam;
        const linkData = outcome?.from?.[originKey] || { count: 0, percent: 0 };

        return linkData.count > 0
          ? [
              {
                id: `spam_${originKey}`,
                side: "left",
                start: stageAnchors[originKey].left,
                end: {
                  x: destinationSlots.spam.x,
                  y: destinationSlots.spam.yByOrigin[originKey],
                },
                label: `${linkData.count} (${linkData.percent}%)`,
                labelX:
                  (stageAnchors[originKey].left.x + destinationSlots.spam.x) /
                    2 -
                  40,
                labelY:
                  (stageAnchors[originKey].left.y +
                    destinationSlots.spam.yByOrigin[originKey]) /
                    2 -
                  12,
              },
            ]
          : [];
      }
    ),
    ...["newInquiryDeals", "contacted", "offered", "invoiced"].flatMap(
      (originKey) => {
        const outcome = outcomes.cold || EMPTY_MONTHLY_FUNNEL.outcomes.cold;
        const linkData = outcome?.from?.[originKey] || { count: 0, percent: 0 };

        return linkData.count > 0
          ? [
              {
                id: `cold_${originKey}`,
                side: "right",
                start: stageAnchors[originKey].right,
                end: {
                  x: destinationSlots.cold.x,
                  y: destinationSlots.cold.yByOrigin[originKey],
                },
                label: `${linkData.count} (${linkData.percent}%)`,
                labelX:
                  (stageAnchors[originKey].right.x + destinationSlots.cold.x) /
                    2 +
                  38,
                labelY:
                  (stageAnchors[originKey].right.y +
                    destinationSlots.cold.yByOrigin[originKey]) /
                    2 -
                  12,
              },
            ]
          : [];
      }
    ),
    ...["newInquiryDeals", "contacted", "offered", "invoiced"].flatMap(
      (originKey) => {
        const outcome = outcomes.partner || EMPTY_MONTHLY_FUNNEL.outcomes.partner;
        const linkData = outcome?.from?.[originKey] || { count: 0, percent: 0 };

        return linkData.count > 0
          ? [
              {
                id: `partner_${originKey}`,
                side: "right",
                start: stageAnchors[originKey].right,
                end: {
                  x: destinationSlots.partner.x,
                  y: destinationSlots.partner.yByOrigin[originKey],
                },
                label: `${linkData.count} (${linkData.percent}%)`,
                labelX:
                  (stageAnchors[originKey].right.x +
                    destinationSlots.partner.x) /
                    2 +
                  40,
                labelY:
                  (stageAnchors[originKey].right.y +
                    destinationSlots.partner.yByOrigin[originKey]) /
                    2 -
                  12,
              },
            ]
          : [];
      }
    ),
  ];

  return (
    <Box sx={{ overflowX: "auto", pb: 1 }}>
      <Box
        sx={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          mx: "auto",
        }}
      >
        <svg
          width={canvasWidth}
          height={canvasHeight}
          style={{ position: "absolute", inset: 0, overflow: "visible" }}
        >
          {connections.map((connection) => (
            <g key={connection.id}>
              <path
                d={buildCurvePath(connection.start, connection.end, connection.side)}
                stroke="#5a5a5a"
                strokeWidth="2.5"
                fill="none"
              />
              <text
                x={connection.labelX}
                y={connection.labelY}
                fontSize="28"
                fontWeight="500"
                fill="#2b2b2b"
                textAnchor="middle"
              >
                {connection.label}
              </text>
            </g>
          ))}
        </svg>

        <FunnelShape
          layout={stageLayouts.newInquiryDeals}
          color={stageLayouts.newInquiryDeals.color}
          label={stages.newInquiryDeals.label}
          count={stages.newInquiryDeals.count}
          percent={stages.newInquiryDeals.percent}
          countFontSize={stageLayouts.newInquiryDeals.countFontSize}
          labelFontSize={stageLayouts.newInquiryDeals.labelFontSize}
        />

        <FunnelShape
          layout={stageLayouts.contacted}
          color={stageLayouts.contacted.color}
          label={stages.contacted.label}
          count={stages.contacted.count}
          percent={stages.contacted.percent}
          countFontSize={stageLayouts.contacted.countFontSize}
          labelFontSize={stageLayouts.contacted.labelFontSize}
        />

        <FunnelShape
          layout={stageLayouts.offered}
          color={stageLayouts.offered.color}
          label={stages.offered.label}
          count={stages.offered.count}
          percent={stages.offered.percent}
          countFontSize={stageLayouts.offered.countFontSize}
          labelFontSize={stageLayouts.offered.labelFontSize}
        />

        <FunnelShape
          layout={stageLayouts.invoiced}
          color={stageLayouts.invoiced.color}
          label={stages.invoiced.label}
          count={stages.invoiced.count}
          percent={stages.invoiced.percent}
          countFontSize={stageLayouts.invoiced.countFontSize}
          labelFontSize={stageLayouts.invoiced.labelFontSize}
        />

        <FunnelShape
          layout={stageLayouts.paid}
          color={stageLayouts.paid.color}
          label={stages.paid.label}
          count={stages.paid.count}
          percent={stages.paid.percent}
          countFontSize={stageLayouts.paid.countFontSize}
          labelFontSize={stageLayouts.paid.labelFontSize}
        />

        <SideOutcomeBox
          layout={outcomeLayouts.spam}
          color={outcomeLayouts.spam.color}
          label={outcomes.spam?.label || "Spam"}
          count={outcomes.spam?.count || 0}
          percent={outcomes.spam?.percent || 0}
        />

        <SideOutcomeBox
          layout={outcomeLayouts.cold}
          color={outcomeLayouts.cold.color}
          label={outcomes.cold?.label || "Cold"}
          count={outcomes.cold?.count || 0}
          percent={outcomes.cold?.percent || 0}
        />

        <SideOutcomeBox
          layout={outcomeLayouts.partner}
          color={outcomeLayouts.partner.color}
          label={outcomes.partner?.label || "Kerja Sama"}
          count={outcomes.partner?.count || 0}
          percent={outcomes.partner?.percent || 0}
        />
      </Box>
    </Box>
  );
}

function getMonthlyRowBackground(row) {
  if (row?.sideOutcomeKey === "spam") return "#f0f0f0";
  if (row?.sideOutcomeKey === "cold") return "#eaf1ff";
  if (row?.sideOutcomeKey === "partner") return "#f1ebff";

  switch (row?.mainStageLabel) {
    case "Paid":
      return "#ffe8ea";
    case "Invoiced":
      return "#fff1df";
    case "Offered":
      return "#fff9dc";
    case "Contacted":
      return "#e8f8ec";
    default:
      return "#e9f1ff";
  }
}

export default function LegalProcess() {
  const nowstate = useSelector((state) => state);

  const defaultPerson = useMemo(() => {
    const currentSales = nowstate?.user?.sales || "";
    return ["Dinda", "Diva"].includes(currentSales) ? currentSales : "-";
  }, [nowstate?.user?.sales]);

  const [selectedPerson, setSelectedPerson] = useState(defaultPerson);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    setSelectedPerson(defaultPerson);
  }, [defaultPerson]);

  const fmt = (d) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Jakarta",
    }).format(d);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB").format(date);
  };

  const formatDateOrDash = (dateString) => formatDate(dateString) || "-";

  const fetchData = async () => {
    if (!selectedStartDate || !selectedEndDate) return;

    try {
      setLoading(true);
      setLoadError("");

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/salesreviewactivity`,
        {
          params: {
            person: selectedPerson,
            startDate: fmt(selectedStartDate),
            endDate: fmt(selectedEndDate),
          },
        }
      );

      setData(response.data);
    } catch (error) {
      console.error("Error fetching salesreviewactivity:", error);
      setLoadError("Gagal memuat data sales review.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPerson, selectedStartDate, selectedEndDate]);

  const handlePersonChange = (event) => {
    setSelectedPerson(event.target.value);
  };

  const handleDateChangeStart = (newDate) => {
    setSelectedStartDate(newDate);
  };

  const handleDateChangeEnd = (newDate) => {
    setSelectedEndDate(newDate);
  };

  const summary = data?.summary || {};
  const lists = data?.lists || {};
  const monthlyFunnel = data?.monthlyFunnel || EMPTY_MONTHLY_FUNNEL;

  const newDeals = lists.newDeals || [];
  const overdueDeals = lists.overdueDeals || [];
  const monthlyFunnelDeals = lists.monthlyFunnelDeals || [];
  const tableRows = lists.tableRows || [];

  const existingNewDeals = summary.existingNewDeals || 0;
  const overdueOpenDeals = summary.overdueOpenDeals || 0;
  const dealsCreatedInRange = summary.dealsCreatedInRange || 0;
  const dealsContactedInRange = summary.dealsContactedInRange || 0;

  const exportTableToExcel = () => {
    if (!tableRows.length) {
      window.alert("Tidak ada data untuk didownload.");
      return;
    }

    const exportRows = tableRows.map((row, index) => ({
      No: index + 1,
      "Nama Lead": row.name || "",
      Phone: row.phone || "",
      Sales: row.sales || "",
      Kategori: row.category || "",
      Tanggal: formatDate(row.dateValue),
      "Deal Status": row.dealStatus || "",
      "Judul Task": row.taskTitle || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 28 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 28 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Review Deals");

    const start = fmt(selectedStartDate);
    const end = fmt(selectedEndDate);
    const salesLabel =
      selectedPerson && selectedPerson !== "-" ? selectedPerson : "Semua";

    XLSX.writeFile(
      workbook,
      `salesreview_deals_${salesLabel}_${start}_to_${end}.xlsx`
    );
  };

  const handleRowClick = (row) => {
    if (!row?.popupData) return;

    setSelectedInquiry(row.popupData);
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedInquiry(null);
  };

  const handleInquirySaved = () => {
    fetchData();
    setPopupOpen(false);
    setSelectedInquiry(null);
  };

  const popupActorId =
    selectedInquiry?.Sales && selectedInquiry?.Sales !== "-"
      ? selectedInquiry.Sales
      : (selectedPerson !== "-" ? selectedPerson : nowstate?.user?.sales) ||
        "sales_unknown";

  const renderMetricCard = (label, value, subtitle) => (
    <Card sx={{ p: 2, height: "100%" }}>
      <Typography sx={{ color: "text.secondary", fontWeight: 600 }}>
        {label}
      </Typography>

      <Typography
        sx={{
          color: "text.primary",
          fontSize: 32,
          fontWeight: 700,
          mt: 1,
        }}
      >
        {value}
      </Typography>

      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Card>
  );

  return (
    <Grid container>
      <Grid
        item
        xl={0.5}
        lg={0.7}
        md={0.9}
        sm={3.2}
        className="leftmenucolor"
      >
        <LeftMenu />
      </Grid>

      <Grid
        item
        xl={11.5}
        lg={11.3}
        md={10.4}
        sm={8.8}
        xs={12}
        sx={{ py: 6, px: { xs: 2, md: 5 } }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                value={selectedPerson}
                onChange={handlePersonChange}
              >
                <MenuItem value="-">Semua</MenuItem>
                <MenuItem value="Dinda">Dinda</MenuItem>
                <MenuItem value="Diva">Diva</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={selectedStartDate}
                onChange={handleDateChangeStart}
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={1}
              container
              alignItems="center"
              justifyContent="center"
            >
              <Typography>to</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={selectedEndDate}
                onChange={handleDateChangeEnd}
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "stretch", md: "flex-end" },
              }}
            >
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={exportTableToExcel}
                disabled={!tableRows.length}
              >
                Download
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {!!loadError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {loadError}
          </Typography>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6} xl={3}>
            {renderMetricCard(
              "Existing New Deals",
              existingNewDeals,
              "Status deal masih New sampai end date"
            )}
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            {renderMetricCard(
              "Overdue Open Deals",
              overdueOpenDeals,
              "Contacted / Offered / Invoiced dengan task overdue"
            )}
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            {renderMetricCard(
              "Deals Created in Range",
              dealsCreatedInRange,
              `${fmt(selectedStartDate)} sampai ${fmt(selectedEndDate)}`
            )}
          </Grid>

          <Grid item xs={12} md={6} xl={3}>
            {renderMetricCard(
              "Deals Contacted in Range",
              dealsContactedInRange,
              `${fmt(selectedStartDate)} sampai ${fmt(selectedEndDate)}`
            )}
          </Grid>
        </Grid>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            New deals dihitung tanpa peduli start date, selama deal dibuat
            sebelum atau pada end date.
            <br />
            Overdue deals dihitung untuk deal status Contacted / Offered /
            Invoiced yang punya task pending dengan due date sebelum tanggal end
            date.
          </Typography>
        </Paper>

        <Typography variant="h6" sx={{ mb: 1 }}>
          Existing New & Overdue Deals
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Baris biru = New · Baris kuning = Overdue
        </Typography>

        <Paper sx={{ width: "100%", mb: 4 }}>
          <TableContainer sx={{ maxHeight: 500, minHeight: 200 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nama Lead</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Sales</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell>Deal Status</TableCell>
                  <TableCell>Judul Task</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        Tidak ada data.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRows.map((row) => {
                    const bg =
                      row.category === "Overdue" ? "#fff8e1" : "#e3f2fd";

                    return (
                      <TableRow
                        key={row.rowKey}
                        hover
                        onClick={() => handleRowClick(row)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: bg,
                        }}
                      >
                        <TableCell>{row.name || ""}</TableCell>
                        <TableCell>{row.phone || ""}</TableCell>
                        <TableCell>{row.sales || ""}</TableCell>
                        <TableCell>{row.category || ""}</TableCell>
                        <TableCell>{formatDate(row.dateValue)}</TableCell>
                        <TableCell>{row.dealStatus || ""}</TableCell>
                        <TableCell>{row.taskTitle || "-"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            DATA Monthly yang perlu diperhatikan
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Funnel ini dihitung dari semua deal yang dateCreated-nya masuk ke
            date range terpilih dan tetap mengikuti filter sales yang sama.
            Contacted / Offered / Invoiced / Paid dibaca dari field tanggal
            deal. Spam / Cold / Kerja Sama dibaca dari field side outcome.
          </Typography>

          <MonthlyFunnelDiagram funnel={monthlyFunnel} />
        </Paper>

        <Typography variant="h6" sx={{ mb: 1 }}>
          Daftar Deal dalam Funnel Monthly
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          1 row = 1 deal. Klik row untuk buka InquiryPops.
        </Typography>

        <Paper sx={{ width: "100%", mb: 4 }}>
          <TableContainer sx={{ maxHeight: 520, minHeight: 200 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1450 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nama Lead</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Sales</TableCell>
                  <TableCell>Deal Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Main Funnel Stage</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Contacted</TableCell>
                  <TableCell>Offered</TableCell>
                  <TableCell>Invoiced</TableCell>
                  <TableCell>Paid</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {monthlyFunnelDeals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        Tidak ada data monthly funnel.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  monthlyFunnelDeals.map((row) => (
                    <TableRow
                      key={row.rowKey}
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: getMonthlyRowBackground(row),
                      }}
                    >
                      <TableCell>{row.name || ""}</TableCell>
                      <TableCell>{row.phone || ""}</TableCell>
                      <TableCell>{row.sales || ""}</TableCell>
                      <TableCell>{row.dealStatus || ""}</TableCell>
                      <TableCell>{formatDateOrDash(row.dateCreated)}</TableCell>
                      <TableCell>{row.mainStageLabel || "-"}</TableCell>
                      <TableCell>{row.sideOutcomeLabel || "-"}</TableCell>
                      <TableCell>{formatDateOrDash(row.dateContacted)}</TableCell>
                      <TableCell>{formatDateOrDash(row.dateOffered)}</TableCell>
                      <TableCell>{formatDateOrDash(row.dateInvoiced)}</TableCell>
                      <TableCell>{formatDateOrDash(row.datePaid)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                New Deals List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {newDeals.length} row
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Overdue Deals List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {overdueDeals.length} row
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {popupOpen && selectedInquiry && (
          <InquiryPops
            open={popupOpen}
            handleClose={handleClosePopup}
            data={selectedInquiry}
            actorId={popupActorId}
            onSave={handleInquirySaved}
          />
        )}
      </Grid>
    </Grid>
  );
}